import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { automationRulesService } from '../../../services/automationRulesService';

const ExecutionLogsPanel = ({ rule, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExecution, setSelectedExecution] = useState(null);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const data = await automationRulesService?.getExecutionLogs(rule?.id);
      setLogs(data || []);
    } catch (error) {
      console.error('Error loading execution logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-success/10 text-success border-success/20',
      failed: 'bg-error/10 text-error border-error/20',
      processing: 'bg-warning/10 text-warning border-warning/20',
      pending: 'bg-muted text-muted-foreground border-border',
      skipped: 'bg-muted text-muted-foreground border-border'
    };
    return colors?.[status] || colors?.pending;
  };

  const calculateEngagementRate = (recipients) => {
    if (!recipients || recipients?.length === 0) return 0;
    const opened = recipients?.filter(r => r?.emailOpened)?.length;
    return Math.round((opened / recipients?.length) * 100);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-heading font-bold text-foreground mb-1">
              Execution Logs
            </h2>
            <p className="text-sm text-muted-foreground">{rule?.ruleName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
                <p className="text-muted-foreground">Loading execution logs...</p>
              </div>
            </div>
          ) : logs?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="History" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                No Execution History
              </h3>
              <p className="text-muted-foreground">
                This rule hasn't been executed yet
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs?.map((log) => (
                <div
                  key={log?.id}
                  className="bg-background border border-border rounded-lg p-4 hover:shadow-md transition-smooth"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                          getStatusColor(log?.executionStatus)
                        }`}>
                          {log?.executionStatus}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {new Date(log?.triggeredAt)?.toLocaleString()}
                        </span>
                      </div>
                      {log?.errorMessage && (
                        <div className="text-sm text-error mt-2">
                          Error: {log?.errorMessage}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Statistics */}
                  <div className="grid grid-cols-4 gap-4 mb-3">
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <div className="text-lg font-bold text-foreground">
                        {log?.matchedClientsCount || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Matched Clients</div>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <div className="text-lg font-bold text-foreground">
                        {log?.emailsSentCount || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Emails Sent</div>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <div className="text-lg font-bold text-success">
                        {calculateEngagementRate(log?.recipients)}%
                      </div>
                      <div className="text-xs text-muted-foreground">Open Rate</div>
                    </div>
                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <div className="text-lg font-bold text-foreground">
                        {log?.recipients?.filter(r => r?.emailClicked)?.length || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Clicks</div>
                    </div>
                  </div>

                  {/* Recipients Toggle */}
                  {log?.recipients?.length > 0 && (
                    <div>
                      <button
                        onClick={() => setSelectedExecution(
                          selectedExecution === log?.id ? null : log?.id
                        )}
                        className="flex items-center gap-2 text-sm text-primary hover:underline"
                      >
                        <Icon 
                          name={selectedExecution === log?.id ? 'ChevronUp' : 'ChevronDown'} 
                          size={16} 
                        />
                        <span>
                          {selectedExecution === log?.id ? 'Hide' : 'Show'} Recipients ({log?.recipients?.length})
                        </span>
                      </button>

                      {selectedExecution === log?.id && (
                        <div className="mt-3 space-y-2">
                          {log?.recipients?.map((recipient) => (
                            <div
                              key={recipient?.id}
                              className="bg-muted/30 border border-border rounded-lg p-3 flex items-center justify-between"
                            >
                              <div>
                                <div className="font-medium text-foreground text-sm">
                                  {recipient?.recipientName}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {recipient?.recipientEmail}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {recipient?.emailSent && (
                                  <div className="flex items-center gap-1 text-xs text-success">
                                    <Icon name="Send" size={12} />
                                    <span>Sent</span>
                                  </div>
                                )}
                                {recipient?.emailOpened && (
                                  <div className="flex items-center gap-1 text-xs text-primary">
                                    <Icon name="Eye" size={12} />
                                    <span>Opened</span>
                                  </div>
                                )}
                                {recipient?.emailClicked && (
                                  <div className="flex items-center gap-1 text-xs text-warning">
                                    <Icon name="MousePointer" size={12} />
                                    <span>Clicked</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-6 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExecutionLogsPanel;